<template>
  <div class="profile-container">
    <div class="page-header">
      <h2 class="page-title">数据统计</h2>
      <p class="page-desc">系统数据概览</p>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-wrapper">
      <el-skeleton :rows="4" animated />
    </div>

    <!-- 数据展示 -->
    <div v-else-if="Object.keys(adminStatsData).length" class="stats-wrapper">
      <!-- 基础统计卡片 -->
      <el-row :gutter="20" class="base-stats-row">
        <el-col :xs="24" :sm="12" :md="8">
          <el-card class="stat-card total" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon">
                <svg-icon icon="user" class="icon" />
              </div>
              <div class="stat-info">
                <div class="stat-label">用户总数</div>
                <div class="stat-value">
                  {{ formatValue(adminStatsData.userCount) }}
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8">
          <el-card class="stat-card info" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon">
                <svg-icon icon="personnel-info" class="icon" />
              </div>
              <div class="stat-info">
                <div class="stat-label">简历总数</div>
                <div class="stat-value">
                  {{ formatValue(adminStatsData.resumeCount) }}
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8">
          <el-card class="stat-card success" shadow="hover">
            <div class="stat-content">
              <div class="stat-icon">
                <svg-icon icon="user" class="icon" />
              </div>
              <div class="stat-info">
                <div class="stat-label">活跃用户数（TODO：不准确）</div>
                <div class="stat-value">
                  {{ formatValue(adminStatsData.activeUserCount) }}
                </div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>

      <!-- 每日统计图表 -->
      <el-row :gutter="20" class="chart-stats-row">
        <!-- 每日新增简历 -->
        <el-col :xs="24" :sm="24" :md="12">
          <el-card class="chart-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <span class="card-title">每日新增简历</span>
              </div>
            </template>
            <div
              v-if="dailyResumesData.length > 0"
              ref="dailyResumesChartRef"
              class="chart-container"
            ></div>
            <el-empty v-else description="暂无数据" :image-size="100" />
          </el-card>
        </el-col>

        <!-- 每日新增用户 -->
        <el-col :xs="24" :sm="24" :md="12">
          <el-card class="chart-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <span class="card-title">每日新增用户</span>
              </div>
            </template>
            <div
              v-if="dailyUsersData.length > 0"
              ref="dailyUsersChartRef"
              class="chart-container"
            ></div>
            <el-empty v-else description="暂无数据" :image-size="100" />
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 空状态 -->
    <el-empty v-else description="暂无数据" />
  </div>
</template>

<script setup>
// 调用 sys 中的 getAdminStats 方法获取数据
import { getAdminStats } from '@/api/sys'
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import * as echarts from 'echarts'

const adminStatsData = ref({})
const loading = ref(false)
const dailyResumesChartRef = ref(null)
const dailyUsersChartRef = ref(null)

let dailyResumesChart = null
let dailyUsersChart = null
let resizeHandlerAdded = false

/**
 * 获取统计数据
 */
const getAdminStatsData = async () => {
  loading.value = true
  try {
    const res = await getAdminStats()
    adminStatsData.value = res

    setTimeout(() => {
      initResumesChart()
      initUsersChart()
    }, 0)
  } catch (error) {
    console.error('获取统计数据失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 格式化数值
 */
const formatValue = (value) => {
  if (typeof value === 'number') {
    return value.toLocaleString()
  }
  return value || 0
}

/**
 * 每日简历数据
 */
const dailyResumesData = computed(() => {
  return adminStatsData.value.dailyStats?.newResumes || []
})

/**
 * 每日用户数据
 */
const dailyUsersData = computed(() => {
  return adminStatsData.value.dailyStats?.newUsers || []
})

/**
 * 初始化每日新增简历图表
 */
const initResumesChart = async () => {
  if (!dailyResumesChartRef.value || dailyResumesData.value.length === 0) {
    return
  }
  const chartElement = dailyResumesChartRef.value
  try {
    if (dailyResumesChart) {
      dailyResumesChart.dispose()
    }
    dailyResumesChart = echarts.init(chartElement)
    const resumesDates = dailyResumesData.value.map((item) => item._id)
    const resumesCounts = dailyResumesData.value.map((item) => item.count)

    const resumesOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: resumesDates,
        axisLabel: {
          rotate: 45,
          interval: 0
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: '新增简历数',
          type: 'bar',
          data: resumesCounts,
          itemStyle: {
            color: '#2563eb'
          },
          emphasis: {
            itemStyle: {
              color: '#1d4ed8'
            }
          }
        }
      ]
    }
    dailyResumesChart.setOption(resumesOption)
    console.log('每日新增简历图表初始化成功', {
      dates: resumesDates,
      counts: resumesCounts
    })
  } catch (error) {
    console.error('初始化每日新增简历图表失败:', error)
  }
}

/**
 * 初始化每日新增用户图表
 */
const initUsersChart = async () => {
  if (!dailyUsersChartRef.value || dailyUsersData.value.length === 0) {
    return
  }

  // 确保 DOM 已经渲染并且容器有尺寸
  await nextTick()
  // 再等待一下确保容器有尺寸
  await new Promise((resolve) => setTimeout(resolve, 100))

  const chartElement = dailyUsersChartRef.value
  if (!chartElement || chartElement.offsetWidth === 0) {
    console.warn('每日新增用户图表容器尺寸为 0，延迟初始化')
    setTimeout(() => initUsersChart(), 200)
    return
  }

  try {
    if (dailyUsersChart) {
      dailyUsersChart.dispose()
    }
    dailyUsersChart = echarts.init(chartElement)
    const usersDates = dailyUsersData.value.map((item) => item._id)
    const usersCounts = dailyUsersData.value.map((item) => item.count)

    const usersOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: usersDates,
        axisLabel: {
          rotate: 45,
          interval: 0
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: '新增用户数',
          type: 'bar',
          data: usersCounts,
          itemStyle: {
            color: '#16a34a'
          },
          emphasis: {
            itemStyle: {
              color: '#15803d'
            }
          }
        }
      ]
    }
    dailyUsersChart.setOption(usersOption)
    console.log('每日新增用户图表初始化成功', {
      dates: usersDates,
      counts: usersCounts
    })
  } catch (error) {
    console.error('初始化每日新增用户图表失败:', error)
  }
}

// 监听每日简历数据变化，初始化图表
watch(
  () => dailyResumesData.value,
  (newVal) => {
    if (newVal && newVal.length > 0) {
      initResumesChart()
    }
  },
  { deep: true, immediate: true }
)

// 监听每日用户数据变化，初始化图表
watch(
  () => dailyUsersData.value,
  (newVal) => {
    if (newVal && newVal.length > 0) {
      initUsersChart()
    }
  },
  { deep: true, immediate: true }
)

/**
 * 处理窗口大小变化
 */
const handleResize = () => {
  if (dailyResumesChart) {
    dailyResumesChart.resize()
  }
  if (dailyUsersChart) {
    dailyUsersChart.resize()
  }
}

onMounted(() => {
  // 添加 resize 监听器
  if (!resizeHandlerAdded) {
    window.addEventListener('resize', handleResize)
    resizeHandlerAdded = true
  }
  getAdminStatsData()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (dailyResumesChart) {
    dailyResumesChart.dispose()
  }
  if (dailyUsersChart) {
    dailyUsersChart.dispose()
  }
})
</script>

<style lang="scss" scoped>
.profile-container {
  padding: 0;
  background-color: transparent;
  min-height: 100%;

  .page-header {
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #303133;
      margin: 0 0 8px 0;
    }

    .page-desc {
      font-size: 14px;
      color: #909399;
      margin: 0;
    }
  }

  .loading-wrapper {
    padding: 20px;
  }

  .stats-wrapper {
    .base-stats-row {
      margin-bottom: 20px;
    }

    .chart-stats-row {
      margin-bottom: 20px;
    }

    .stat-card {
      margin-bottom: 20px;

      ::v-deep .el-card__body {
        padding: 18px;
      }

      .stat-content {
        display: flex;
        align-items: center;
        gap: 16px;

        .stat-icon {
          width: 64px;
          height: 64px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;

          .icon {
            width: 22px;
            height: 22px;
            color: currentColor;
          }
        }

        .stat-info {
          flex: 1;
          min-width: 0;

          .stat-label {
            font-size: 14px;
            color: #909399;
            margin-bottom: 8px;
            font-weight: 500;
          }

          .stat-value {
            font-size: 28px;
            font-weight: 700;
            color: #303133;
            line-height: 1.2;
            word-break: break-all;
          }
        }
      }

    }

    .chart-card {
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .card-title {
          font-size: 16px;
          font-weight: 700;
          color: var(--admin-color-text);
        }
      }

      .chart-container {
        width: 100%;
        height: 350px;
        min-height: 350px;
      }
    }
  }
}
</style>
